import { z } from "zod";

// The cluster Auto Tune tab reuses the jobs status / history / savings
// schemas verbatim (the BE returns the identical shapes); only the
// recommendation list and the toggle request/response are
// cluster-specific. Re-export the shared ones so callers import from a
// single module.
export {
  AutoTuneStatusResponseSchema,
  AutoTuneHistoryResponseSchema,
  AutoTuneSavingsTrendsResponseSchema,
} from "@/pages/jobs/auto-tune/types";
export type {
  AutoTuneStatusResponse,
  AutoTuneHistoryResponse,
  AutoTuneSavingsTrendsResponse,
} from "@/pages/jobs/auto-tune/types";

// The two cluster recommendation actions exposed as drawer toggles.
// ``resize`` tightens the autoscaling worker bounds (Worker Sizing);
// ``auto_terminate`` lowers ``autotermination_minutes`` (Auto Termination).
export const ClusterRecTypeSchema = z.enum(["resize", "auto_terminate"]);
export type ClusterRecType = z.infer<typeof ClusterRecTypeSchema>;

// One all-purpose-cluster recommendation from ``databricks_recommendation``
// (``entity_type='all_purpose'``). ``enabled`` is the resolved sub-toggle
// state from the cluster's auto-tune configuration row.
export const ClusterAutoTuneRecommendationSchema = z.object({
  entity_id: z.string(),
  entity_name: z.string().nullable().optional(),
  rec_type: z.string(),
  rec_source: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  owned_by: z.string().nullable().optional(),
  workspace_id: z.string().nullable().optional(),
  // SKU knobs — symmetric current vs recommended. ``resize`` carries
  // ``{min_workers, max_workers}``; ``auto_terminate`` carries
  // ``{auto_termination_minutes}``.
  current_config: z.record(z.string(), z.unknown()).default({}),
  recommended_config: z.record(z.string(), z.unknown()).default({}),
  current_cost_per_hr_dbu: z.number().nullable().optional(),
  recommended_cost_per_hr_dbu: z.number().nullable().optional(),
  estimated_savings_pct: z.number().nullable().optional(),
  recommendation_summary: z.string().nullable().optional(),
  reasoning: z.string().nullable().optional(),
  inference_start_date: z.string().nullable().optional(),
  inference_end_date: z.string().nullable().optional(),
  enabled: z.boolean().default(false),
});

export type ClusterAutoTuneRecommendation = z.infer<typeof ClusterAutoTuneRecommendationSchema>;

export const ClusterAutoTuneRecommendationsResponseSchema = z.object({
  recommendations: z.array(ClusterAutoTuneRecommendationSchema).default([]),
  total_count: z.number().default(0),
});

export type ClusterAutoTuneRecommendationsResponse = z.infer<
  typeof ClusterAutoTuneRecommendationsResponseSchema
>;

export const ClusterAutoTuneTogglePreflightSchema = z.object({
  blocked: z.boolean().default(false),
  reason: z.string().nullable().optional(),
  actionable_message: z.string().nullable().optional(),
});

export const ClusterAutoTuneToggleResponseSchema = z.object({
  auto_tune_mode: z.string().nullable().optional(),
  audit_event_id: z.number().nullable().optional(),
  entity_name: z.string().nullable().optional(),
  preflight: ClusterAutoTuneTogglePreflightSchema.default({ blocked: false }),
});

export type ClusterAutoTuneToggleResponse = z.infer<typeof ClusterAutoTuneToggleResponseSchema>;
